import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JournalList() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/cms/journal`, { withCredentials: true });
        setEntries(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    await axios.delete(`${API}/api/cms/journal/${id}`, { withCredentials: true });
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="journal-list">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-start justify-between mb-10">
          <div>
            <Link to="/advisor/content" className="text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A] transition-colors">
              Content
            </Link>
            <h1 className="font-serif text-3xl text-[#1A1A1A] mt-4">Journal</h1>
          </div>
          <button
            onClick={() => navigate('/advisor/content/journal/new')}
            className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
            data-testid="journal-new-btn"
          >
            <Plus size={14} /> New Entry
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-[#999]">Loading...</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#E8E4DC] rounded-lg">
            <p className="text-sm text-[#999] mb-4">No journal entries yet</p>
            <button
              onClick={() => navigate('/advisor/content/journal/new')}
              className="text-sm text-[#1A1A1A] underline"
            >
              Create your first entry
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="journal-entries">
            {entries.map(entry => (
              <div key={entry.id} className="flex items-center justify-between p-5 bg-white border border-[#E8E4DC] rounded-lg group" data-testid={`journal-entry-${entry.id}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-serif text-base text-[#1A1A1A] truncate">{entry.title}</h3>
                    {entry.status === 'published' ? (
                      <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                        <Eye size={10} /> Published
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] text-[#999] bg-[#F5F3EE] px-2 py-0.5 rounded-full">
                        <EyeOff size={10} /> Draft
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#999]">
                    {entry.category && <span className="mr-3">{entry.category}</span>}
                    {entry.slug}
                  </p>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => navigate(`/advisor/content/journal/${entry.id}`)}
                    className="p-2 text-[#6B6B6B] hover:text-[#1A1A1A]"
                    data-testid={`journal-edit-${entry.id}`}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="p-2 text-[#6B6B6B] hover:text-red-500"
                    data-testid={`journal-delete-${entry.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
